import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class HDCartUpsell extends HTMLElement {
  #lastCart = null;
  #activeFetch = null;

  connectedCallback() {
    this.onCartUpdate = this.onCartUpdate.bind(this);
    this.onClick = this.onClick.bind(this);

    this.addEventListener('click', this.onClick);
    document.addEventListener('cart:update', this.onCartUpdate);
    document.addEventListener('discount:update', this.onCartUpdate);

    this.refresh();
    this.loadCart();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('cart:update', this.onCartUpdate);
    document.removeEventListener('discount:update', this.onCartUpdate);
  }

  onCartUpdate(event) {
    const payload = event?.detail?.resource || event?.detail?.data || null;
    if (payload) this.#lastCart = payload;
    if (payload && typeof payload.item_count === 'number') {
      this.dataset.cartCount = String(payload.item_count);
    } else if (payload && typeof payload.itemCount === 'number') {
      this.dataset.cartCount = String(payload.itemCount);
    }

    if (payload && typeof payload.original_total_price === 'number') {
      this.dataset.cartTotalPrice = String(payload.original_total_price);
    }

    this.refresh(payload);
  }

	  onClick(event) {
	    const target = event.target instanceof HTMLElement ? event.target : null;
	    if (!target) return;

	    const jumpTrigger = target.closest('[data-hd-cart-upsell-jump]');
	    if (jumpTrigger) {
      const rail = this.querySelector('[data-hd-cart-upsell-rail]');
      if (!rail) return;
	
	      event.preventDefault();
	      rail.scrollIntoView({ behavior: 'smooth', block: 'start' });
	      return;
	    }

	    const offerTrigger = target.closest('[data-hd-cart-upsell-cta]');
	    if (offerTrigger instanceof HTMLAnchorElement && this.dataset.state === 'ready') {
	      event.preventDefault();
	      this.applyDiscount(offerTrigger);
	    }
	  }

	  async applyDiscount(trigger) {
	    const sectionId = this.dataset.sectionId;
	    if (!sectionId) {
	      window.location.href = trigger.href;
	      return;
	    }

	    if (this.#activeFetch) this.#activeFetch.abort();
	    this.#activeFetch = new AbortController();
	    trigger.setAttribute('aria-busy', 'true');
	    trigger.textContent = 'Applying...';

	    const existingDiscounts = this.existingDiscountCodes();
	    if (!existingDiscounts.some((code) => code.toLowerCase() === this.discountTitle.toLowerCase())) {
	      existingDiscounts.push(this.discountTitle);
	    }

	    try {
	      const config = fetchConfig('json', {
	        body: JSON.stringify({
	          discount: existingDiscounts.join(','),
	          sections: [sectionId],
	        }),
	      });

	      const response = await fetch(Theme.routes.cart_update_url, {
	        ...config,
	        signal: this.#activeFetch.signal,
	      });

	      const data = await response.json();
	      this.#lastCart = data;

	      const codeEntry = this.getDiscountEntry(data);
	      if (codeEntry && codeEntry.applicable === false) {
	        this.dataset.discountError = 'true';
	        this.refresh(data);
	        return;
	      }

	      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
	      if (data.sections?.[sectionId]) {
	        morphSection(sectionId, data.sections[sectionId]);
	      } else {
	        this.refresh(data);
	      }
	    } catch (error) {
	      if (error?.name !== 'AbortError') {
	        this.dataset.discountError = 'true';
	        this.refresh(this.#lastCart);
	      }
	    } finally {
	      this.#activeFetch = null;
	      trigger.removeAttribute('aria-busy');
	    }
	  }

  async loadCart() {
    try {
      const response = await fetch('/cart.js', {
        headers: {
          Accept: 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) return;

      const cart = await response.json();
      this.#lastCart = cart;
      this.dataset.cartCount = String(cart?.item_count ?? 0);
      this.dataset.cartTotalPrice = String(cart?.original_total_price ?? 0);
      this.refresh(cart);
    } catch (error) {
      // Keep the server-rendered state if the cart fetch fails.
    }
  }

  refresh(resource = null) {
    const cart = resource || this.#lastCart;
    const state = this.getState(cart);
	    const message = this.querySelector('[data-hd-cart-upsell-message]');
	    const title = this.querySelector('[data-hd-cart-upsell-title]');
	    const status = this.querySelector('[data-hd-cart-upsell-status]');
    const hint = this.querySelector('[data-hd-cart-upsell-hint]');
    const code = this.querySelector('[data-hd-cart-upsell-code]');
    const progress = this.querySelector('.hd-cart-upsell__progress span');
    const cta = this.querySelector('[data-hd-cart-upsell-cta]');
    const footnote = this.querySelector('[data-hd-cart-upsell-footnote]');

    this.dataset.state = state.state;
    this.dataset.discountApplied = state.discountApplied ? 'true' : 'false';
	    this.setAttribute('data-remaining-items', String(state.remainingItems ?? 0));

	    if (title instanceof HTMLElement) title.textContent = state.title;
	    if (message instanceof HTMLElement) message.textContent = state.message;
    if (status instanceof HTMLElement) status.textContent = state.status;
    if (hint instanceof HTMLElement) hint.textContent = state.hint;
    if (code instanceof HTMLElement) {
      code.textContent = state.codeLabel;
      code.dataset.active = state.discountApplied ? 'true' : 'false';
    }
    if (progress instanceof HTMLElement) progress.style.width = `${state.progressPercent}%`;
    if (cta instanceof HTMLAnchorElement) {
      cta.href = state.ctaHref;
      cta.textContent = state.ctaLabel;
      cta.removeAttribute('aria-busy');
    }
    if (footnote instanceof HTMLElement) footnote.textContent = state.footnote;
  }

  getState(resource = null) {
    const cart = resource || this.#lastCart;
    const savingsLabel = this.dataset.savingsLabel?.trim() || 'Rs. 50 off';
    const offerMode = this.dataset.offerMode || 'quantity';
    const threshold = Math.max(1, Number(this.dataset.threshold || 2));
    const minSubtotal = Math.max(0, Number(this.dataset.minSubtotal || 0));
    const cartCount = Math.max(0, Number(cart?.item_count ?? cart?.itemCount ?? this.dataset.cartCount ?? 0));
    const cartTotal = Math.max(0, Number(cart?.original_total_price ?? this.dataset.cartTotalPrice ?? 0));
    const discountApplied = this.isDiscountApplied(cart);
    const discountEntry = this.getDiscountEntry(cart);
    const remainingItems = Math.max(0, threshold - cartCount);
    const remainingSubtotal = Math.max(0, minSubtotal - cartTotal);
    const progressPercent =
      offerMode === 'subtotal'
        ? Math.min(100, minSubtotal > 0 ? Math.round((cartTotal / minSubtotal) * 100) : 100)
        : Math.min(100, Math.round((cartCount / threshold) * 100));

    if (discountEntry && discountEntry.applicable === false) {
      return {
        state: 'setup',
        discountApplied: false,
        progressPercent,
	        codeLabel: this.discountTitle,
	        title: this.baseTitle,
	        status: 'Setup needed',
        hint: 'Shopify knows the code, but the discount is not active for this cart yet.',
        message: this.errorText || 'The offer is visible, but the discount rule still needs to be activated in Shopify Admin.',
        footnote: 'Once Shopify marks this discount as active, the cart total will update automatically.',
        ctaLabel: 'See tees below',
        ctaHref: '#hd-cart-upsell-rail',
        remainingItems,
      };
    }

    if (discountApplied) {
      return {
        state: 'applied',
        discountApplied: true,
        progressPercent,
	        codeLabel: 'Applied',
	        title: `${this.discountTitle} applied`,
	        status: `${savingsLabel} active`,
        hint: 'The reduced total is already reflected in your bag.',
        message: this.appliedText || 'Savings locked. You can checkout whenever you are ready.',
        footnote: 'The automatic discount is already reflected below.',
        ctaLabel: 'Checkout with savings',
        ctaHref: '/checkout',
        remainingItems,
      };
    }

    if (offerMode === 'subtotal' && remainingSubtotal > 0) {
      return {
        state: 'progress',
        discountApplied: false,
        progressPercent,
	        codeLabel: this.discountTitle,
	        title: this.baseTitle,
	        status: `${this.formatMoney(cartTotal)} / ${this.formatMoney(minSubtotal)}`,
        hint: `Add ${this.formatMoney(remainingSubtotal)} more to unlock the offer.`,
        message: `Add ${this.formatMoney(remainingSubtotal)} more to unlock ${savingsLabel}.`,
        footnote: `The save unlocks once your cart reaches ${this.formatMoney(minSubtotal)}.`,
        ctaLabel: 'See tees below',
        ctaHref: '#hd-cart-upsell-rail',
        remainingItems,
      };
    }

    if (offerMode === 'quantity' && remainingItems > 0) {
      return {
        state: 'progress',
        discountApplied: false,
        progressPercent,
	        codeLabel: this.discountTitle,
	        title: this.baseTitle,
	        status: `${cartCount} / ${threshold} tees`,
        hint: 'Add one more tee from the rail below.',
        message: `Add ${remainingItems} more tee${remainingItems === 1 ? '' : 's'} to unlock ${savingsLabel}.`,
        footnote: `The save unlocks at ${threshold} tees.`,
        ctaLabel: 'See tees below',
        ctaHref: '#hd-cart-upsell-rail',
        remainingItems,
      };
    }

    return {
      state: 'ready',
      discountApplied: false,
      progressPercent,
	        codeLabel: this.discountTitle,
	        title: this.baseTitle,
	        status: 'Offer unlocked',
	        hint: 'Tap once to lock the code into this cart.',
	        message: this.offerText || `Your bag qualifies. Claim ${savingsLabel} before checkout.`,
	        footnote: 'The code will be attached to your cart and reflected before checkout.',
	        ctaLabel: `Apply ${savingsLabel}`,
	        ctaHref: `/discount/${encodeURIComponent(this.discountTitle)}?redirect=/cart`,
      remainingItems: 0,
    };
  }

  isDiscountApplied(resource = null) {
    const cart = resource || this.#lastCart;
    const normalized = this.discountTitle.toLowerCase();
    const resourceCodes = [];

    if (Array.isArray(cart?.discount_codes)) {
      for (const entry of cart.discount_codes) {
        if (typeof entry?.code === 'string' && entry.applicable !== false) resourceCodes.push(entry.code);
        else if (typeof entry?.title === 'string') resourceCodes.push(entry.title);
      }
    }

    if (Array.isArray(cart?.cart_level_discount_applications)) {
      for (const entry of cart.cart_level_discount_applications) {
        if (typeof entry?.title === 'string') resourceCodes.push(entry.title);
      }
    }

    return resourceCodes.some((value) => value.toLowerCase() === normalized);
  }

	  getDiscountEntry(resource = null) {
    const cart = resource || this.#lastCart;
    const normalized = this.discountTitle.toLowerCase();
    const entries = [];

    if (Array.isArray(cart?.discount_codes)) entries.push(...cart.discount_codes);
    if (Array.isArray(cart?.cart_level_discount_applications)) {
      for (const entry of cart.cart_level_discount_applications) {
        if (entry) entries.push(entry);
      }
    }

    return entries.find((entry) => {
      const title = typeof entry?.title === 'string' ? entry.title : typeof entry?.code === 'string' ? entry.code : '';
      return title.toLowerCase() === normalized;
    }) || null;
  }

  formatMoney(value) {
    const amount = Number(value || 0);
    if (!Number.isFinite(amount)) return '';
    const locale = document.documentElement.lang || navigator.language || 'en-IN';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      }).format(amount / 100);
    } catch (error) {
      return `Rs. ${(amount / 100).toFixed(0)}`;
    }
  }

  get discountTitle() {
    return this.dataset.discountCode?.trim() || 'ADDTEE50';
  }

	  get offerText() {
	    return this.dataset.offerText?.trim() || '';
	  }

	  existingDiscountCodes(resource = null) {
	    const cart = resource || this.#lastCart;
	    const codes = [];

	    if (Array.isArray(cart?.discount_codes)) {
	      for (const entry of cart.discount_codes) {
	        const code = typeof entry?.code === 'string' ? entry.code : typeof entry?.title === 'string' ? entry.title : '';
	        if (code) codes.push(code);
	      }
	    }

	    if (Array.isArray(cart?.cart_level_discount_applications)) {
	      for (const entry of cart.cart_level_discount_applications) {
	        if (typeof entry?.title === 'string') codes.push(entry.title);
	      }
	    }

	    return [...new Set(codes)];
	  }

	  get baseTitle() {
	    const heading = this.querySelector('[data-hd-cart-upsell-title]');
	    return this.dataset.offerTitle?.trim() || heading?.textContent?.trim() || 'Add one more T-shirt and get Rs. 50 off';
	  }

  get appliedText() {
    return this.dataset.appliedText?.trim() || '';
  }

  get errorText() {
    return this.dataset.errorText?.trim() || '';
  }
}

if (!customElements.get('hd-cart-upsell')) {
  customElements.define('hd-cart-upsell', HDCartUpsell);
}
